import { useEffect } from 'react';

const FEATURES = [
  {
    icon: '🎯',
    color: '#7C3AED',
    title: '1v1 Number Battles',
    desc: 'Pick a secret number 1–100. Race your friend to crack theirs first. Higher / lower hints fly back instantly.',
  },
  {
    icon: '🎙️',
    color: '#22C55E',
    title: 'Live Voice Chat',
    desc: 'Real-time audio in every match. Trash talk, celebrate, mind-game your opponent — all from inside the app.',
  },
  {
    icon: '🔥',
    color: '#F97316',
    title: 'Wager Real Stakes',
    desc: 'Bet anything that matters: chores, coffee, a back rub. Pick the deal up front, settle it after the round.',
  },
  {
    icon: '🤝',
    color: '#EC4899',
    title: 'Build Rivalries',
    desc: 'Add friends by username or invite code. Watch the head-to-head record grow with every duel.',
  },
  {
    icon: '⚡',
    color: '#0EA5E9',
    title: 'Daily Streaks',
    desc: 'Play a friend every day to keep your fire alive. Snapchat-style streak counter that brings you back.',
  },
  {
    icon: '📊',
    color: '#FFD700',
    title: 'Lifetime Stats',
    desc: 'Win rate, longest streak, total games, wager record — every rivalry tracked down to the last guess.',
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Add a friend', desc: 'Use a username, friend code, or share an invite link. Both of you online → ready to play in 5 seconds.' },
  { step: '02', title: 'Pick the wager (optional)', desc: 'Set the stakes before the duel. Coffee, dishes, last word in an argument — whatever makes losing sting.' },
  { step: '03', title: 'Lock your number', desc: 'Both players secretly choose 1 through 100. Once locked, the duel starts immediately.' },
  { step: '04', title: 'Guess and win', desc: 'Take turns guessing. Higher / lower hints come instantly. First to crack the code takes the round — and the wager.' },
];

const SCREENSHOTS = [
  { src: '/screenshots/home.png', label: 'Home — your rivals at a glance' },
  { src: '/screenshots/gameplay.png', label: 'Live match with voice + chat' },
  { src: '/screenshots/deals.png', label: 'Deals settled, scores tracked' },
  { src: '/screenshots/friends.png', label: 'Friends list with online status' },
  { src: '/screenshots/stats.png', label: 'Head-to-head rivalry stats' },
];

export default function Home() {
  useEffect(() => {
    // Smooth-scroll for in-page anchor links — handles route load
    // hitting "/#features" directly.
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  return (
    <main>
      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <section className="sky-bg" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div className="container" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 60,
          alignItems: 'center',
          paddingTop: 60,
        }}>
          <div className="hero-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
            gap: 60,
            alignItems: 'center',
          }}>
            <div>
              <span className="eyebrow on-dark">⚡ Live multiplayer</span>
              <h1 style={{ color: 'white', marginBottom: 22 }}>
                Outsmart your friends in <span style={{
                  display: 'inline-block',
                  background: 'linear-gradient(180deg, #FFE066 0%, #F97316 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>60-second</span> number duels
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.92)',
                fontSize: '1.15rem',
                lineHeight: 1.65,
                marginBottom: 34,
                maxWidth: 540,
              }}>
                GuessUp turns "higher or lower" into a heart-pounding showdown. Live voice chat, fun wagers, and daily rivalries — built for friends who love to compete.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <a href="#download" className="btn-3d dark">
                  <span style={{ fontSize: '1.4rem' }}></span>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 500, opacity: 0.8 }}>Download on the</span>
                    <span style={{ fontSize: '1.05rem' }}>App Store</span>
                  </span>
                </a>
                <a href="#download" className="btn-3d dark">
                  <span style={{ fontSize: '1.4rem' }}>▶</span>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 500, opacity: 0.8 }}>Get it on</span>
                    <span style={{ fontSize: '1.05rem' }}>Google Play</span>
                  </span>
                </a>
              </div>

              <div style={{
                display: 'flex',
                gap: 28,
                marginTop: 36,
                color: 'rgba(255, 255, 255, 0.92)',
                fontSize: '0.95rem',
              }}>
                <Stat label="Avg. match" value="<60s" />
                <Stat label="Voice chat" value="Built-in" />
                <Stat label="Cost" value="Free" />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="phone-frame" style={{ maxWidth: 320 }}>
                <img src="/screenshots/home.png" alt="GuessUp home screen" />
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 880px) {
            .hero-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 36px !important; }
          }
        `}</style>
      </section>

      {/* ─── Trust strip ────────────────────────────────────────── */}
      <section style={{ padding: '32px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 24,
          textAlign: 'center',
        }}>
          {[
            ['Real-time multiplayer', '🟢'],
            ['No ads mid-match', '🚫'],
            ['Voice chat included', '🎙️'],
            ['Free to play', '✨'],
          ].map(([label, emoji]) => (
            <div key={label} style={{ color: 'var(--ink-muted)', fontWeight: 600, fontSize: '0.95rem' }}>
              <span style={{ marginRight: 8, fontSize: '1.2rem' }}>{emoji}</span>{label}
            </div>
          ))}
        </div>
      </section>

      {/* ─── Features ───────────────────────────────────────────── */}
      <section id="features" className="section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span className="eyebrow">Features</span>
            <h2 style={{ marginBottom: 14 }}>Everything a friend duel should be</h2>
            <p style={{ color: 'var(--ink-muted)', maxWidth: 580, margin: '0 auto', fontSize: '1.05rem' }}>
              Six things working together to turn a quick number game into a real rivalry.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
          }}>
            {FEATURES.map((f) => (
              <div className="feature-card" key={f.title}>
                <div className="icon-bubble" style={{
                  background: `linear-gradient(180deg, ${withAlpha(f.color, 0.18)} 0%, ${withAlpha(f.color, 0.32)} 100%)`,
                  color: f.color,
                  border: `1px solid ${withAlpha(f.color, 0.28)}`,
                  boxShadow: `0 6px 0 ${withAlpha(f.color, 0.18)}`,
                }}>
                  {f.icon}
                </div>
                <h3 style={{ marginBottom: 8 }}>{f.title}</h3>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.96rem', lineHeight: 1.65 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ──────────────────────────────────────── */}
      <section id="how-it-works" className="section" style={{ background: 'var(--surface-alt)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span className="eyebrow">How it works</span>
            <h2 style={{ marginBottom: 14 }}>Up and running in 30 seconds</h2>
            <p style={{ color: 'var(--ink-muted)', maxWidth: 540, margin: '0 auto', fontSize: '1.05rem' }}>
              Tap, invite, lock your number. Then it's a race.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 20,
          }}>
            {HOW_IT_WORKS.map((h) => (
              <div key={h.step} style={{
                background: 'white',
                border: '1px solid var(--line)',
                borderRadius: 20,
                padding: 28,
              }}>
                <div style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 800,
                  fontSize: '2.5rem',
                  background: 'linear-gradient(180deg, var(--sky-top), var(--sky-bot))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: 8,
                  lineHeight: 1,
                }}>
                  {h.step}
                </div>
                <h4 style={{ marginBottom: 8 }}>{h.title}</h4>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.94rem', lineHeight: 1.65 }}>{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Screenshots showcase ──────────────────────────────── */}
      <section id="screenshots" className="section sky-bg">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span className="eyebrow on-dark">In the app</span>
            <h2 style={{ color: 'white', marginBottom: 14 }}>Built for the friends you love to outsmart</h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.88)', maxWidth: 580, margin: '0 auto', fontSize: '1.05rem' }}>
              Five screens that turn a number guess into a story.
            </p>
          </div>

          <div style={{
            display: 'flex',
            gap: 24,
            overflowX: 'auto',
            padding: '12px 0 24px',
            scrollSnapType: 'x mandatory',
          }}>
            {SCREENSHOTS.map((s) => (
              <div
                key={s.src}
                style={{
                  flex: '0 0 auto',
                  scrollSnapAlign: 'center',
                  width: 240,
                  textAlign: 'center',
                }}
              >
                <div className="phone-frame" style={{ width: '100%' }}>
                  <img src={s.src} alt={s.label} loading="lazy" />
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem', marginTop: 14, marginBottom: 0 }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why GuessUp ──────────────────────────────────────── */}
      <section className="section">
        <div className="container" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 60,
          alignItems: 'center',
        }} >
          <div className="why-grid">
            <div className="phone-frame" style={{ maxWidth: 300, margin: '0 auto' }}>
              <img src="/screenshots/gameplay.png" alt="GuessUp gameplay" />
            </div>
          </div>
          <div>
            <span className="eyebrow">Why GuessUp</span>
            <h2 style={{ marginBottom: 18 }}>Built for the small group of people you actually want to play with</h2>
            <p style={{ color: 'var(--ink-soft)', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: 14 }}>
              Most multiplayer mini-games are slow, ad-heavy, or built around random matchmaking with strangers. GuessUp ditches all of that.
            </p>
            <ul style={{ paddingLeft: 0, listStyle: 'none', marginTop: 24 }}>
              {[
                'Fast matches with people you know',
                'Voice chat baked in — no Discord required',
                'Wagers that make every round count',
                'Stats per friend, not a generic leaderboard',
                'No pay-to-win, no ads in the middle of a duel',
              ].map((line) => (
                <li key={line} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  padding: '8px 0', color: 'var(--ink-soft)', fontSize: '0.98rem',
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    background: 'linear-gradient(180deg, #4ADE80, #22C55E)',
                    color: 'white', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontWeight: 800, fontSize: '0.78rem',
                    boxShadow: '0 3px 0 #15803D', marginTop: 2,
                  }}>✓</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <style>{`
          @media (max-width: 880px) {
            .why-grid + div { grid-row: 1; }
          }
        `}</style>
      </section>

      {/* ─── Final CTA ─────────────────────────────────────────── */}
      <section id="download" className="section sky-bg" style={{ textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: 'white', marginBottom: 16 }}>Ready to challenge your first rival?</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.92)', fontSize: '1.05rem', maxWidth: 540, margin: '0 auto 32px' }}>
            Free to download, free to play. No ads mid-match. Just you, your friends, and the cleanest number duel on your phone.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
            {/* TODO: replace these with real store URLs once published. */}
            <a href="https://apps.apple.com/app/guessup" className="btn-3d dark" target="_blank" rel="noopener noreferrer">
              <span style={{ fontSize: '1.4rem' }}></span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 500, opacity: 0.8 }}>Download on the</span>
                <span style={{ fontSize: '1.05rem' }}>App Store</span>
              </span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.guessup" className="btn-3d dark" target="_blank" rel="noopener noreferrer">
              <span style={{ fontSize: '1.4rem' }}>▶</span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 500, opacity: 0.8 }}>Get it on</span>
                <span style={{ fontSize: '1.05rem' }}>Google Play</span>
              </span>
            </a>
          </div>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem', marginTop: 24 }}>
            Free · iOS 14+ / Android 8+ · No payment required
          </p>
        </div>
      </section>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{
        fontFamily: "'Baloo 2', sans-serif",
        fontWeight: 800,
        fontSize: '1.6rem',
        color: 'white',
        lineHeight: 1,
      }}>{value}</div>
      <div style={{
        fontSize: '0.78rem',
        color: 'rgba(255, 255, 255, 0.78)',
        marginTop: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}>{label}</div>
    </div>
  );
}

function withAlpha(hex, alpha) {
  // tiny helper — turn #RRGGBB into rgba() for the icon-bubble tints
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
